import { Injectable, Injector } from '@angular/core';
import * as wizeMobileApi from '@wize-mobile-api';
import * as chatApi from '@chat-api';
import * as authenticationApi from '@authentication-api';
import * as tcAccountApi from '@topcard-account-api';
import * as tcTransactionApi from '@topcard-transaction-api';
import * as newsApi from '@news-api';
import { select, Store } from '@ngrx/store';
import { selectAuthenticationResult, selectIsAuthenticated } from '@qvs/core/auth';
import { selectEnvironment } from '@qvs/core/env';
import { backendAvailableOrHasChanged, newsConfigurationHasChanged } from '~/modules/app/slice';
import { HttpHeaders } from '@angular/common/http';
import { AuthenticationResult, Environment } from '@qvs/core';
import { filter, withLatestFrom } from 'rxjs/operators';
import { SixMarketDataService } from '~/modules/instrument/six-market-data.service';
import { selectCgeNewsToken } from '@qvs/core/remoteConfig';

@Injectable({
  providedIn: 'root'
})
export class ApiConfigurationService {
  public headersWithBearerToken: { [key: string]: string };
  public environment: Environment;
  private wizeConfiguration: wizeMobileApi.Configuration;
  private chatConfiguration: chatApi.Configuration;
  private authenticationConfiguration: authenticationApi.Configuration;
  private tcAccountConfiguration: tcAccountApi.Configuration;
  private tcTransactionConfiguration: tcTransactionApi.Configuration;
  private newsConfiguration: newsApi.Configuration;

  constructor(
    private readonly store: Store,
    private readonly injector: Injector
  ) {}

  public init(): Promise<void> {
    this.store
      .select(selectEnvironment)
      .pipe(filter((env) => !!env?.wizeApiBasePath))
      .subscribe((env) => {
        this.environment = env;
        this.updateConfiguration(env);
      });

    this.store
      .select(selectIsAuthenticated)
      .pipe(withLatestFrom(this.store.select(selectAuthenticationResult)))
      .subscribe(([authenticated, authenticationResult]) => {
        if (authenticated) {
          this.updateConfiguration(this.environment, authenticationResult);
        } else {
          this.updateConfiguration(this.environment, {
            ...authenticationResult,
            cookie: null,
            statusToken: null,
            chatToken: null
          });
        }
      });

    this.store.pipe(select(selectCgeNewsToken)).subscribe((token) => {
      if (token) {
        this.updateNewsConfiguration(this.environment, token);
      }
    });

    return Promise.resolve();
  }

  public updateConfiguration(env?: Environment, authenticationResult?: AuthenticationResult) {
    this.wizeConfiguration = new wizeMobileApi.Configuration({
      basePath: env?.wizeApiBasePath,
      withCredentials: true,
      accessToken: null
    });
    this.chatConfiguration = new chatApi.Configuration({
      basePath: env?.chatApiBasePath,
      withCredentials: true,
      accessToken: null
    });
    this.authenticationConfiguration = new authenticationApi.Configuration({
      basePath: env?.authenticationBasePath
    });
    this.tcAccountConfiguration = new tcAccountApi.Configuration({
      basePath: `${env?.topCardApi?.basePath}${env?.topCardApi?.accountsApi}`,
      withCredentials: true
    });
    this.tcTransactionConfiguration = new tcTransactionApi.Configuration({
      basePath: `${env?.topCardApi?.basePath}${env?.topCardApi?.transactionsApi}`,
      withCredentials: true
    });

    this.headersWithBearerToken = {};
    let chatHeaders = new HttpHeaders({
      //eslint-disable-next-line @typescript-eslint/naming-convention
      'User-Agent': env.userAgent
    });
    let wizeHeaders = new HttpHeaders({
      //eslint-disable-next-line @typescript-eslint/naming-convention
      'User-Agent': env.userAgent
    });

    if (authenticationResult?.chatToken) {
      chatHeaders = new HttpHeaders({
        Authorization: `Bearer ${authenticationResult.chatToken}`,
        //eslint-disable-next-line @typescript-eslint/naming-convention
        'User-Agent': env.userAgent
      });
      // cookie usage in QVS only
      if (authenticationResult.cookie) {
        this.headersWithBearerToken.Cookie = authenticationResult.cookie;
      }
      this.headersWithBearerToken.Authorization = `Bearer ${authenticationResult.chatToken}`;
      this.chatConfiguration.accessToken = authenticationResult.chatToken;
    }

    // accessToken usage in CGE only
    if (authenticationResult?.accessToken) {
      this.wizeConfiguration.accessToken = authenticationResult.accessToken;
      wizeHeaders = new HttpHeaders({
        Authorization: `Bearer ${authenticationResult.accessToken}`,
        //eslint-disable-next-line @typescript-eslint/naming-convention
        'User-Agent': env.userAgent
      });
    }

    wizeMobileApi.APIS.forEach((serviceName) => {
      this.injector.get(serviceName).configuration = this.wizeConfiguration;
      this.injector.get(serviceName).defaultHeaders = wizeHeaders;
    });

    chatApi.APIS.forEach((serviceName) => {
      this.injector.get(serviceName).configuration = this.chatConfiguration;
      this.injector.get(serviceName).defaultHeaders = chatHeaders;
    });

    authenticationApi.APIS.forEach((serviceName) => {
      this.injector.get(serviceName).configuration = this.authenticationConfiguration;
    });

    this.injector.get(SixMarketDataService).baseUrl = env?.sixApiBasePath;

    for (const service of tcAccountApi.APIS) {
      this.injector.get(service).configuration = this.tcAccountConfiguration;
    }

    for (const service of tcTransactionApi.APIS) {
      this.injector.get(service).configuration = this.tcTransactionConfiguration;
    }

    this.store.dispatch(backendAvailableOrHasChanged());
  }

  updateNewsConfiguration(env: Environment, cgeNewsBearerToken: string) {
    const newsHeaders = new HttpHeaders({
      Authorization: `Bearer ${cgeNewsBearerToken}`,
      'User-Agent': env?.userAgent
    });

    this.newsConfiguration = new newsApi.Configuration({
      basePath: env?.newsBasePath,
      withCredentials: false
    });

    newsApi.APIS.forEach((serviceName) => {
      this.injector.get(serviceName).configuration = this.newsConfiguration;
      this.injector.get(serviceName).defaultHeaders = newsHeaders;
    });

    this.store.dispatch(newsConfigurationHasChanged());
  }
}
